import { useState } from 'preact/hooks';
import { read } from '../api/client.js';
import { invalidate, taskResourceKey, useResource } from '../api/resource.js';
import { spaceContextKey, useSpaceContext } from '../api/space-context.js';
import { useSession } from '../session/session.jsx';
import { Button, ButtonLink, Chip } from '../ui/primitives.jsx';
import { Empty, Resource } from '../ui/states.jsx';
import './space-context.css';

export function SpaceContextPane({ spaceId }) {
  const session = useSession();
  if (!spaceId || !session.nodeId || session.status !== 'ready') return <Empty title="읽을 공간과 참여 Node를 선택해 주세요." />;
  return <ContextPage key={JSON.stringify([session.nodeId, spaceId])} spaceId={spaceId} nodeId={session.nodeId} />;
}

function ContextPage({ spaceId, nodeId }) {
  const resource = useSpaceContext(spaceId, nodeId);
  const [historyOpen, setHistoryOpen] = useState(false);
  return <section class="work work--fluid space-context">
    <header class="work__head"><div class="work__head-text">
      <h1 class="t-view">공동 맥락</h1>
      <p class="t-meta">함께 향하는 목적과, 그 기준을 선택한 이유를 읽습니다.</p>
    </div></header>
    <div class="work__body"><Resource resource={resource} loadingLabel="공동 맥락을 읽는 중">{(data) => <>
      <p class="t-meta">사람이 지정한 기준 · 전체 참여자의 개별 동의를 뜻하지 않음</p>
      {resource.isStale ? <p role="status" class="t-meta">이전 읽기입니다. 최신 기준을 확인하는 중입니다.</p> : null}
      {data.context ? <>
        <section class="space-context__section" aria-labelledby="shared-purpose">
          <div class="row"><Chip tone="accepted">v{data.context.versionNo} · 지정된 수락 판</Chip><span class="t-meta">지정 이력 #{data.revision}</span></div>
          <h2 id="shared-purpose" class="t-section">함께 향하는 목적</h2>
          <p class="t-body space-context__text">{data.context.selectedVersion.purpose}</p>
          <ContextPrefix label="확정한 사실" value={data.context.selectedVersion.confirmed} />
          <ContextPrefix label="아직 모르는 것" value={data.context.selectedVersion.openQuestions} />
          <ContextPrefix label="참조 ID" value={data.context.selectedVersion.referenceIds} ids />
        </section>
        <section class="space-context__section" aria-label="공동 맥락의 출처">
          <h2 class="t-section">이 기준을 선택한 이유</h2>
          <p class="t-body space-context__text">{data.context.rationale}</p>
          <ContextProvenance item={data.context} spaceId={spaceId} nodeId={nodeId} />
          {data.context.sourceHasNewerVersion ? <p class="t-meta">출처 Task에 새 수락 판 v{data.context.currentTaskVersionNo}이 있습니다. 이 공간의 기준은 v{data.context.versionNo}으로 유지됩니다.</p> : null}
          <p class="t-meta">새 Task 판이 수락되어도 이 기준은 자동으로 바뀌지 않습니다.</p>
        </section>
      </> : <UnsetContext spaceId={spaceId} />}
      <section class="space-context__section" aria-label="공동 맥락 지정 이력">
        <h2 class="t-section">기준을 이어온 기록</h2>
        <p class="t-meta">과거 지정의 출처와 근거입니다. 현재 기준과 다를 수 있으며, 당시 기록은 바뀌지 않습니다.</p>
        {historyOpen ? <ContextHistory spaceId={spaceId} nodeId={nodeId} /> :
          <Button size="sm" onClick={() => setHistoryOpen(true)}>지정 이력 읽기</Button>}
      </section>
    </>}</Resource></div>
  </section>;
}

export function SpaceContextSummary({ spaceId }) {
  const { nodeId } = useSession();
  const resource = useSpaceContext(spaceId, nodeId);
  return <section class="space-context space-context__summary" aria-label="공동 맥락 요약">
    <div class="row"><h2 class="t-section">공동 맥락</h2><ButtonLink size="xs" tone="ghost" href={`/s/${spaceId}/context`}>기준과 출처 읽기</ButtonLink></div>
    <Resource resource={resource} loadingLabel="공동 맥락을 읽는 중" compact>{(data) => data.context ? <>
      <p class="t-body space-context__text">{data.context.selectedVersion.purpose}</p>
      <p class="t-meta">사람이 지정한 기준 · v{data.context.versionNo}{resource.isStale ? ' · 최신 기준 확인 중' : ''}</p>
    </> : <p class="t-meta">아직 공동 맥락이 지정되지 않았습니다. Task에서 수락된 이해를 읽고 이어갈 기준을 살펴보세요.</p>}</Resource>
  </section>;
}

function UnsetContext({ spaceId }) {
  return <section class="space-context__section"><h2 class="t-section">아직 공동 맥락이 지정되지 않았습니다.</h2>
    <p class="t-body">기존 Task에서 수락된 이해를 읽어보세요. Space 오너가 그중 한 판을 근거와 함께 공동 기준으로 지정할 수 있습니다.</p>
    <ButtonLink size="sm" href={`/s/${spaceId}/tasks`}>Task에서 수락된 이해 읽기</ButtonLink>
  </section>;
}

function ContextPrefix({ label, value, ids = false }) {
  return <div class="space-context__prefix"><h3 class="t-body-strong">{label}</h3>
    <p class="t-meta">전체 {value.total}건 중 {value.items.length}건 표시{value.incomplete ? ' · 일부만 표시 (불완전)' : ''}</p>
    {value.items.length ? <ul>{value.items.map((item, index) => <li class="t-body space-context__text" key={item.id ?? index}>{ids ? item : item.text}</li>)}</ul> : <p class="t-meta">표시할 항목이 없습니다.</p>}
  </div>;
}

function ContextProvenance({ item, spaceId, nodeId }) {
  return <><dl class="space-context__provenance t-meta">
    <div><dt>출처 Task · 판</dt><dd>{item.taskId} · v{item.versionNo}</dd></div>
    <div><dt>수락 출처 제안</dt><dd>{item.promotedFromProposalId}</dd></div>
    <div><dt>수락된 때</dt><dd>{date(item.acceptedAt)}</dd></div>
    <div><dt>지정한 참여 Node</dt><dd>{item.adoptedByNodeId}</dd></div>
    <div><dt>지정한 때</dt><dd>{date(item.adoptedAt)}</dd></div>
  </dl>
    <SelectedVersion key={JSON.stringify([nodeId, spaceId, item.taskId, item.versionNo])}
      nodeId={nodeId} spaceId={spaceId} taskId={item.taskId} versionNo={item.versionNo} />
    <ButtonLink size="sm" href={`/s/${spaceId}/task/${item.taskId}/reentry`}>출처 Task의 현재 맥락 읽기</ButtonLink>
  </>;
}

function SelectedVersion({ nodeId, spaceId, taskId, versionNo }) {
  const [reading, setReading] = useState(false);
  // 큰 본문은 명시적으로 연 뒤에만 읽는다. 수동 캐시도 참여 Node 범위를 나눈다.
  const key = taskResourceKey(spaceId, taskId, `version:${versionNo}:reader:${JSON.stringify(nodeId)}`);
  const resource = useResource(reading ? key : null, async (signal) => {
    const data = await read('task-version', { nodeId, signal, query: { spaceId, taskId, versionNo } });
    if (data?.taskId !== taskId || data.versionNo !== versionNo || typeof data.purpose !== 'string'
      || !['confirmed', 'openQuestions'].every((field) => Array.isArray(data[field])
        && data[field].every((item) => typeof item?.text === 'string'))
      || !Array.isArray(data.referenceIds) || !data.referenceIds.every((id) => typeof id === 'string')) {
      throw new Error('선택한 수락 판의 전체 내용을 확인하지 못했습니다.');
    }
    return data;
  });
  if (!reading) return <Button size="sm" onClick={() => {
    invalidate(key); setReading(true);
  }}>지정된 v{versionNo} 전체 읽기</Button>;
  return <section class="stack stack--tight" aria-label={`선택한 수락 판 v${versionNo} 전체 내용`}>
    <h3 class="t-body-strong">{taskId} · 지정된 v{versionNo} 전체 내용</h3>
    <Resource resource={resource} loadingLabel={`지정된 v${versionNo} 전체 내용을 읽는 중`}>{(data) => <>
      <p class="t-body space-context__text">{data.purpose}</p>
      <ContextPrefix label="확정한 사실" value={{ items: data.confirmed, total: data.confirmed.length, incomplete: false }} />
      <ContextPrefix label="아직 모르는 것" value={{ items: data.openQuestions, total: data.openQuestions.length, incomplete: false }} />
      <ContextPrefix label="참조 ID" value={{ items: data.referenceIds, total: data.referenceIds.length, incomplete: false }} ids />
      <p class="t-meta">수락 출처 제안: {data.promotedFromProposalId}</p>
    </>}</Resource>
    <Button size="sm" onClick={() => setReading(false)}>전체 내용 닫기</Button>
  </section>;
}

function ContextHistory({ spaceId, nodeId }) {
  const [page, setPage] = useState({ before: null, number: 1 });
  const resource = useResource(`${spaceContextKey(spaceId)}:history:${page.before ?? 'first'}`, async (signal) => {
    const data = await read('space-context-history', { nodeId, signal, query: { spaceId, limit: 10,
      ...(page.before === null ? {} : { beforeRevision: page.before }) } });
    if (data?.schemaVersion !== 1 || data.spaceId !== spaceId || !Array.isArray(data.items)) throw new Error('이 공간의 지정 이력을 확인하지 못했습니다.');
    return data;
  }, { quiet: true, identity: nodeId });
  return <Resource resource={resource} loadingLabel="지정 이력을 읽는 중">{(data) => <>
    <p class="t-meta">{page.number}페이지 · 최대 10건{resource.isStale ? ' · 이전 읽기, 최신 기록 확인 중' : ''}</p>
    {data.items.length ? <ol class="space-context__history">{data.items.map((item) => <li key={item.revision}>
      <h3 class="t-body-strong">지정 이력 #{item.revision} · v{item.versionNo}</h3>
      <p class="t-body space-context__text">{item.rationale}</p><ContextProvenance item={item} spaceId={spaceId} nodeId={nodeId} />
    </li>)}</ol> : <p class="t-meta">지정 이력이 없습니다.</p>}
    <nav class="row" aria-label="지정 이력 페이지">
      <Button size="sm" disabled={page.before === null} onClick={() => setPage({ before: null, number: 1 })}>최근 이력</Button>
      <Button size="sm" disabled={resource.isStale || !data.hasMore || !Number.isInteger(data.nextBeforeRevision) || data.nextBeforeRevision === page.before}
        onClick={() => setPage({ before: data.nextBeforeRevision, number: page.number + 1 })}>이전 지정 10건</Button>
    </nav>
  </>}</Resource>;
}

function date(value) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '기록 시각 확인 필요' : new Intl.DateTimeFormat('ko-KR', { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
}
